import React, { useContext, useEffect, useRef, useState } from 'react'

import { API, PhiAPI } from 'API'
import { UserAccessPermissionsEnum } from 'constants/userAccessPermissionsEnum'
import { CasesPageContext } from 'containers/CasesPage/CasesPageContext'
import { RequestToasterContext } from 'containers/Providers'
import { getUserAuthorities } from 'core/auth'
import { getPhiPhase } from 'core/phiPhase'
import { differenceInSeconds } from 'date-fns'
import { Case } from 'domains/case'
import { UserGender } from 'domains/user'
import { StandardErrorResponse } from 'types/APIResponses'

const editPhiPatientNotAllowed =
  [
    'DUAL_WRITE_AND_READ_FROM_DTX_MODE',
    'DUAL_WRITE_AND_READ_FROM_PHI_FALLBACK_MODE',
    'DUAL_WRITE_READ_ONLY_FROM_PHI_MODE',
    'PHI_ONLY_MODE',
  ].includes(getPhiPhase()) &&
  !getUserAuthorities().includes(UserAccessPermissionsEnum.DTX_PHI_DATA_ACCESS_WRITE)

const inPhiReadPhase = [
  'DUAL_WRITE_AND_READ_FROM_PHI_FALLBACK_MODE',
  'DUAL_WRITE_READ_ONLY_FROM_PHI_MODE',
  'PHI_ONLY_MODE',
].includes(getPhiPhase())

const inStrictReadPhase = ['DUAL_WRITE_READ_ONLY_FROM_PHI_MODE', 'PHI_ONLY_MODE'].includes(
  getPhiPhase(),
)

export const CasePatient = () => {
  const { casesPageState, getCase } = useContext(CasesPageContext)
  const { id, patient, phiPatientId } = casesPageState.userCase as Case
  const { requestStatusRef } = useContext(RequestToasterContext)

  const [patientFirstName, setPatientFirstName] = useState(patient.personFirstName)
  const handleChangePatientFistName = (event: React.ChangeEvent<HTMLInputElement>) =>
    setPatientFirstName(event.target.value)

  const [patientLastName, setPatientLastName] = useState(patient.personLastName)
  const handleChangePatientLastName = (event: React.ChangeEvent<HTMLInputElement>) =>
    setPatientLastName(event.target.value)

  const [patientGender, setPatientGender] = useState<UserGender>(patient.personGender)
  const handleChangePatientGender = (event: React.ChangeEvent<HTMLSelectElement>) =>
    setPatientGender(event.target.value as UserGender)

  const [patientBirthDate, setPatientBirthDate] = useState(patient.personBirthDate)
  const handleChangePatientBirthDate = (event: React.ChangeEvent<HTMLInputElement>) =>
    differenceInSeconds(new Date(), new Date(event.target.value)) < 0
      ? requestStatusRef.current?.showAlert('Enter valid date', 'danger')
      : setPatientBirthDate(event.target.value)

  const handleUpdatePatientName = () => {
    requestStatusRef.current?.startProgress('Renaming patient...', 'secondary')
    API.updateCasePatientById({
      id,
      phiPatientId,
      firstName: patientFirstName,
      lastName: patientLastName,
    })
      .then(() => {
        getCase()
        requestStatusRef.current?.showAlert('Updated', 'success')
      })
      .catch((error: StandardErrorResponse) => {
        requestStatusRef.current?.showAlert(error.response.data.error, 'danger')
      })
  }

  const handleUpdatePatientGender = () => {
    requestStatusRef.current?.startProgress('Updating...', 'secondary')
    API.updateCasePatientById({
      id: id,
      phiPatientId,
      gender: encodeURIComponent(patientGender),
    })
      .then(() => {
        getCase()
        requestStatusRef.current?.showAlert('Updated', 'success')
      })
      .catch((error: StandardErrorResponse) => {
        requestStatusRef.current?.showAlert(error.response.data.error, 'danger')
      })
  }

  const handleUpdatePatientBirthDate = () => {
    requestStatusRef.current?.startProgress('Updating...', 'secondary')
    API.updateCasePatientById({
      id,
      phiPatientId,
      birthDate: patientBirthDate,
    })
      .then(() => {
        getCase()
        requestStatusRef.current?.showAlert('Updated', 'success')
      })
      .catch((error: StandardErrorResponse) => {
        requestStatusRef.current?.showAlert(error.response.data.error, 'danger')
      })
  }

  const unknownGender = patientGender === '*****' || patientGender === ''
  const unknownBirthDate = patientBirthDate === '*****' || patientBirthDate === ''
  const fetchedPhiIdRef = useRef<number | null>(null)

  useEffect(() => {
    const shouldFetch = inPhiReadPhase && phiPatientId && fetchedPhiIdRef.current !== phiPatientId

    if (!shouldFetch) return
    fetchedPhiIdRef.current = phiPatientId

    PhiAPI.getPatientFromPHI(phiPatientId)
      .then(({ personFirstName, personLastName, personGender, personBirthDate }) => {
        setPatientFirstName(personFirstName)
        setPatientLastName(personLastName)
        setPatientGender(personGender)
        setPatientBirthDate(personBirthDate)
      })
      .catch((error) => {
        if (inStrictReadPhase) {
          setPatientFirstName('')
          setPatientLastName('')
          setPatientGender('')
          setPatientBirthDate('')
          requestStatusRef.current?.showAlert(error.response.data.error, 'danger')
        }
      })
  }, [phiPatientId, requestStatusRef])

  return (
    <tr>
      <td>Patient</td>
      <td>
        <form>
          <input
            type="text"
            value={patientFirstName}
            className="mr-2 w-25"
            onChange={handleChangePatientFistName}
            disabled={editPhiPatientNotAllowed}
          />
          <input
            type="text"
            value={patientLastName}
            className="w-25"
            onChange={handleChangePatientLastName}
            disabled={editPhiPatientNotAllowed}
          />
          <button
            onClick={handleUpdatePatientName}
            className="float-right"
            type="button"
            disabled={editPhiPatientNotAllowed}>
            Rename
          </button>
        </form>
        <form className="mt-2 border-top pt-2">
          <span className="mr-2 d-inline-block w-25">Birth date</span>

          {unknownBirthDate ? (
            <input type="text" value={patientBirthDate} readOnly className="w-25 text-muted" />
          ) : (
            <input
              type="date"
              value={patientBirthDate}
              onChange={handleChangePatientBirthDate}
              disabled={editPhiPatientNotAllowed}
              max={new Date().toISOString().slice(0, 10)}
              className="w-25"
            />
          )}

          <button
            onClick={handleUpdatePatientBirthDate}
            className="float-right"
            type="button"
            disabled={editPhiPatientNotAllowed}>
            Update birth date
          </button>
        </form>
        <form className="mt-2 border-top pt-2">
          <span className="mr-2 d-inline-block w-25">Gender</span>

          {unknownGender ? (
            <input type="text" value={patientGender} readOnly className="w-25 text-muted" />
          ) : (
            <select
              value={patientGender}
              onChange={handleChangePatientGender}
              className="w-25"
              disabled={editPhiPatientNotAllowed}>
              {Object.entries(UserGender).map(([key, value], index) => (
                <option key={`${key}-${index}`} value={key}>
                  {value}
                </option>
              ))}
            </select>
          )}
          <button
            onClick={handleUpdatePatientGender}
            className="float-right"
            type="button"
            disabled={editPhiPatientNotAllowed}>
            Update gender
          </button>
        </form>
      </td>
    </tr>
  )
}
